/**
 * How each entry type reads on screen — titles, detail lines, and dot colours.
 * Mirrors the design's TYPES map. Pure; shared by the timeline, the stats, and
 * the toasts so a bottle is described the same way everywhere.
 */
import {
  type BottleUnit,
  formatAmount,
  formatWeight,
  gramsFromKilograms,
  gramsFromPounds,
  poundsAndOunces,
  type WeightUnit,
  weightProblem,
} from "@babia/checks";
import { dur } from "./time.ts";

export type EntryType = "feed" | "diaper" | "sleep" | "meds" | "note";
export type DiaperKind = "wet" | "dirty" | "both";
export type NoteKind =
  | "general"
  | "bath"
  | "weight"
  | "poop"
  | "pee"
  | "feeding"
  | "sleep"
  | "health";
export type DoseUnit = "ml" | "drop" | "tsp" | "oz" | "pill" | "mg";

/** A medicine's amount, e.g. 2.5 ml or 1 drop. */
export interface Dose {
  amount: number;
  unit: DoseUnit;
}

export interface EntryLike {
  id: string;
  babyId: string;
  type: EntryType;
  at: Date;
  endedAt: Date | null;
  /** Feed only, as stored: ounces, whatever unit the household logs in. */
  amountOz: number | null;
  diaperKind: DiaperKind | null;
  label: string | null;
  note: string | null;
  /** Notes only. */
  noteKind: NoteKind | null;
  /** Meds only, with `doseUnit`: both or neither. */
  doseAmount: number | null;
  doseUnit: DoseUnit | null;
  /** Weight notes only: whole grams; null for a weight in words alone. */
  weightGrams?: number | null;
}

export const TYPE_TITLE: Record<EntryType, string> = {
  feed: "Bottle",
  diaper: "Change",
  sleep: "Sleep",
  meds: "Meds",
  note: "Note",
};

/**
 * The emoji each log button, sheet, and toast carries. Decorative: always
 * next to the words, and hidden from screen readers.
 */
export const TYPE_EMOJI: Record<EntryType, string> = {
  feed: "🍼",
  diaper: "🧷",
  sleep: "😴",
  meds: "💊",
  note: "📝",
};
/** Ending a nap: the sleep tile while a baby is asleep. */
export const WAKE_EMOJI = "🌅";

/** Tailwind background class for the 7px timeline dot (tokens in styles.css). */
export const TYPE_DOT: Record<EntryType, string> = {
  feed: "bg-accent",
  diaper: "bg-accent-2",
  sleep: "bg-yellow",
  meds: "bg-neutral-600",
  note: "bg-neutral-400",
};

export const DIAPER_EMOJI: Record<DiaperKind, string> = {
  wet: "💧",
  dirty: "💩",
  both: "💧💩",
};

export const DIAPER_LABEL: Record<DiaperKind, string> = {
  wet: "Wet",
  dirty: "Dirty",
  both: "Wet + dirty",
};

/** The change sheet's choices, in order. */
export const DIAPER_KINDS: DiaperKind[] = ["wet", "dirty", "both"];

/** The note sheet's choices, in order; "general" is the default. */
export const NOTE_KINDS: NoteKind[] = [
  "general",
  "bath",
  "weight",
  "poop",
  "pee",
  "feeding",
  "sleep",
  "health",
];

/** The note sheet's chip for each kind. */
export const NOTE_KIND_LABEL: Record<NoteKind, string> = {
  general: "General",
  bath: "Bath",
  weight: "Weight",
  poop: "Poop",
  pee: "Pee",
  feeding: "Feeding",
  sleep: "Sleep",
  health: "Health",
};

/** What a note is called on its own: a timeline row, a toast, the report. */
export const NOTE_KIND_TITLE: Record<NoteKind, string> = {
  general: "Note",
  bath: "Bath",
  weight: "Weight",
  poop: "Poop note",
  pee: "Pee note",
  feeding: "Feeding note",
  sleep: "Sleep note",
  health: "Health note",
};

export const NOTE_KIND_EMOJI: Record<NoteKind, string> = {
  general: "📝",
  bath: "🛁",
  weight: "⚖️",
  poop: "💩",
  pee: "💧",
  feeding: "🍼",
  sleep: "🌙",
  health: "🌡️",
};

/** The meds sheet's unit chips and the settings' unit menu, in order. */
export const DOSE_UNITS: DoseUnit[] = ["ml", "drop", "tsp", "oz", "pill", "mg"];

/** A unit's name on a chip or in a menu. */
export const DOSE_UNIT_LABEL: Record<DoseUnit, string> = {
  ml: "ml",
  drop: "drops",
  tsp: "tsp",
  oz: "oz",
  pill: "pills",
  mg: "mg",
};

/** What the dose stepper's – and + change, by unit. */
export const DOSE_STEP: Record<DoseUnit, number> = {
  ml: 0.25,
  drop: 1,
  tsp: 0.25,
  oz: 0.25,
  pill: 0.5,
  mg: 5,
};

/** The largest dose in any unit (the server's MAX_DOSE). */
export const MAX_DOSE = 1000;

/** Two decimals, as the server stores a dose (its `roundDose`): 1.005 is 1.01, not 1. */
export function roundDose(amount: number): number {
  return Math.round(Number((amount * 100).toPrecision(12))) / 100;
}

/** "2.5 ml", "1 drop", "2 drops", "0.5 pill". */
export function formatDose(amount: number, unit: DoseUnit): string {
  const plural = amount > 1 && (unit === "drop" || unit === "pill");
  return `${roundDose(amount)} ${unit}${plural ? "s" : ""}`;
}

/** A typed dose amount ("2.5", "1,25") as a number, or null if it isn't a usable one. */
export function parseDose(text: string): number | null {
  const trimmed = text.trim().replace(",", ".");
  if (!/^(\d+\.?\d*|\.\d+)$/.test(trimmed)) return null;
  const amount = roundDose(Number(trimmed));
  return amount > 0 && amount <= MAX_DOSE ? amount : null;
}

/** "Tylenol 2.5 ml", or the name alone when no dose was logged. */
export function medsSummary(e: {
  label?: string | null;
  doseAmount?: number | null;
  doseUnit?: DoseUnit | null;
}): string {
  const name = e.label ?? TYPE_TITLE.meds;
  return typeof e.doseAmount === "number" && e.doseUnit
    ? `${name} ${formatDose(e.doseAmount, e.doseUnit)}`
    : name;
}

/** "Weight 9 lb 4.2 oz" for a weight with its number; else the entry's name. */
export function noteSummary(
  e: { type: EntryType; noteKind?: NoteKind | null; weightGrams?: number | null },
  unit: WeightUnit,
): string {
  const name = entryName(e);
  return typeof e.weightGrams === "number" ? `${name} ${formatWeight(e.weightGrams, unit)}` : name;
}

/** "9 lb 4.2 oz · 38th percentile", or the weight alone without one. */
export function weightDetail(grams: number, unit: WeightUnit, percentile?: string | null): string {
  const weight = formatWeight(grams, unit);
  return percentile ? `${weight} · ${percentile}` : weight;
}

/** How a weight note's number reads: the household's unit, and its percentile once known. */
export interface WeightShown {
  unit: WeightUnit;
  percentile?: string | null;
}

type Kinded = { type: EntryType; noteKind?: NoteKind | null };

/** "Bottle", "Bath", "Poop note": what an entry is, whatever its state. */
export function entryName(e: Kinded): string {
  return e.type === "note" ? NOTE_KIND_TITLE[e.noteKind ?? "general"] : TYPE_TITLE[e.type];
}

/** The emoji a toast or sheet shows for an entry; a note's is its kind's. */
export function entryEmoji(e: Kinded): string {
  return e.type === "note" ? NOTE_KIND_EMOJI[e.noteKind ?? "general"] : TYPE_EMOJI[e.type];
}

export function entryTitle(e: EntryLike): string {
  const base = entryName(e);
  return e.type === "sleep" && !e.endedAt ? `${base} (now)` : base;
}

/** Who a save for every baby covers, as its button says it: "both", or "all 3". */
export function forAllWords(count: number): string {
  return count === 2 ? "both" : `all ${count}`;
}

/** Names as a sentence lists them: "Luna and Leo", "Ada, Bea, and Cy". */
export function namesText(names: readonly string[]): string {
  if (names.length <= 2) return names.join(" and ");
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

/**
 * The row's second line; a bottle's amount in the household's `unit`, and a
 * weight's number in `weight`'s ("9 lb 4.2 oz · 38th percentile — at the checkup").
 */
export function entryDetail(e: EntryLike, unit: BottleUnit, weight?: WeightShown): string {
  switch (e.type) {
    case "feed": {
      const amount = formatAmount(e.amountOz ?? 0, unit);
      return e.label ? `${amount} — ${e.label}` : amount;
    }
    case "diaper":
      return e.diaperKind ? DIAPER_LABEL[e.diaperKind] : "";
    case "sleep":
      return e.endedAt ? dur(e.at, e.endedAt) : "asleep now";
    case "meds":
      return e.doseAmount !== null && e.doseUnit
        ? `${e.label ?? ""} — ${formatDose(e.doseAmount, e.doseUnit)}`
        : (e.label ?? "");
    case "note": {
      if (typeof e.weightGrams !== "number" || !weight) return e.note ?? "";
      const number = weightDetail(e.weightGrams, weight.unit, weight.percentile);
      return e.note ? `${number} — ${e.note}` : number;
    }
  }
}

/** A weight as typed in the note sheet: pounds and ounces, or kilograms. */
export interface WeightInput {
  unit: WeightUnit;
  pounds: string;
  ounces: string;
  kilograms: string;
}

export type ParsedWeight = { grams: number } | { empty: true } | { invalid: string };

const DECIMAL = /^(\d+\.?\d*|\.\d+)$/;
const decimal = (text: string) => {
  const trimmed = text.trim().replace(",", ".");
  return DECIMAL.test(trimmed) ? Number(trimmed) : null;
};

/**
 * A weight in both units' boxes, so the other unit chip shows the same
 * weight: 4200 g is 9 lb 4.2 oz, and 4.2 kg.
 */
export function weightInputOf(grams: number | null | undefined, unit: WeightUnit): WeightInput {
  if (typeof grams !== "number") return { unit, pounds: "", ounces: "", kilograms: "" };
  const { pounds, ounces } = poundsAndOunces(grams);
  return {
    unit,
    pounds: String(pounds),
    ounces: String(ounces),
    kilograms: String(Number((grams / 1000).toFixed(3))),
  };
}

/**
 * The weight boxes after a tap on `unit`'s chip. The typed number goes along
 * (9 lb 8 oz shows as 4.309 kg), unless that unit's boxes already show the
 * same weight: a saved 4200 g stays 4.2 kg, not 9 lb 4.2 oz read back as 4201 g.
 */
export function inUnit(input: WeightInput, unit: WeightUnit): WeightInput {
  const typed = parseWeightInput(input);
  if (!("grams" in typed)) return { ...input, unit };
  const there = parseWeightInput({ ...input, unit });
  const shown = (grams: number) => formatWeight(grams, input.unit);
  return "grams" in there && shown(there.grams) === shown(typed.grams)
    ? { ...input, unit }
    : weightInputOf(typed.grams, unit);
}

/**
 * The typed weight in whole grams, nothing typed yet, or why it can't be one.
 * Pounds are whole; ounces take a decimal (a comma works too) and stay under
 * 16 beside pounds.
 */
export function parseWeightInput(input: WeightInput): ParsedWeight {
  let grams: number;
  if (input.unit === "kg") {
    if (!input.kilograms.trim()) return { empty: true };
    const kg = decimal(input.kilograms);
    if (kg === null) return { invalid: "Type the kilograms as a number, like 4.2" };
    grams = gramsFromKilograms(kg);
  } else {
    const poundsText = input.pounds.trim();
    const ouncesText = input.ounces.trim();
    if (!poundsText && !ouncesText) return { empty: true };
    if (poundsText && !/^\d+$/.test(poundsText)) return { invalid: "Pounds are a whole number" };
    const pounds = poundsText ? Number(poundsText) : 0;
    const ounces = ouncesText ? decimal(ouncesText) : 0;
    if (ounces === null) return { invalid: "Type the ounces as a number, like 4.5" };
    if (poundsText && ounces >= 16) return { invalid: "Ounces stay under 16" };
    grams = gramsFromPounds(pounds, ounces);
  }
  const problem = weightProblem(grams);
  return problem ? { invalid: `${problem[0]!.toUpperCase()}${problem.slice(1)}` } : { grams };
}
